import dayjs from 'dayjs';
import {
  normalizeRecurringSettings,
  RECURRING_START_TIME,
  RECURRING_END_TIME,
} from 'src/modules/campaigns/helpers';
import type { ScheduledType, RecurringPeriod, RecurringSettings } from '../types';

type RecurringMessageLike = {
  position?: number;
  scheduledType: ScheduledType;
  delay?: string | null;
  time?: string | null;
};

export const getRecurringIntervalMin = (period: RecurringPeriod): number => (
  period === 'minute' ? 5 : 1
);

const getRecurringDateTimeValue = (
  date: string,
  time: string,
) => {
  if (!date) {
    return null;
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return null;
  }
  const parsedDateTime = dayjs(`${parsedDate.format('YYYY-MM-DD')}T${time}`);

  return parsedDateTime.isValid() ? parsedDateTime : null;
};

export const normalizeRecurringMessages = <T extends RecurringMessageLike>(
  messages: T[],
  createFallbackMessage: () => T,
): T[] => {
  const firstMessage = messages[0] || createFallbackMessage();

  return [
    {
      ...firstMessage,
      position: 1,
      scheduledType: 'immediate' as ScheduledType,
      delay: null,
      time: null,
    },
  ];
};

export const validateRecurringSettingsOrder = (
  recurringSettings?: RecurringSettings,
): Record<string, string> => {
  const validationErrors: Record<string, string> = {};
  const settings = recurringSettings ? normalizeRecurringSettings(recurringSettings) : null;

  if (!settings) {
    return validationErrors;
  }

  if (
    settings.dateStart &&
    settings.dateEnd &&
    getRecurringDateTimeValue(settings.dateStart, RECURRING_START_TIME)?.isAfter(
      getRecurringDateTimeValue(settings.dateEnd, RECURRING_END_TIME),
    )
  ) {
    validationErrors['recurringSettings.dateEnd'] =
      'End date cannot be before start date.';
  }

  return validationErrors;
};

export const validateRecurringSettings = (
  recurringSettings?: RecurringSettings,
): Record<string, string> => {
  const validationErrors: Record<string, string> = {};
  const settings = recurringSettings ? normalizeRecurringSettings(recurringSettings) : null;

  if (!settings) {
    validationErrors['recurringSettings.period'] = 'Recurring period is required.';
    return validationErrors;
  }

  if (!settings.dateStart) {
    validationErrors['recurringSettings.dateStart'] = 'Start date is required.';
  }

  if (!settings.daysOfWeek.length) {
    validationErrors['recurringSettings.daysOfWeek'] = 'Select at least one day.';
  }

  return {
    ...validationErrors,
    ...validateRecurringSettingsOrder(settings),
  };
};
